import { useQueryClient } from '@tanstack/react-query';
import {
  ActiveSourcingChannelDetails,
  SourcingChannelStatus,
  SourcingOpeningChannelsResponse,
} from 'api-clients/monolith';
import { useCallback, useState } from 'react';

import { useSetCampaignStatus } from '@/hooks/use-set-campaign-status';
import { queryKeys } from '@/query-keys';

export type SetReachPublisherStatus = (
  sourcingPurchaseId: string,
  publisherId: string,
  publisherStatus: SourcingChannelStatus,
) => void;

export const useReachChannels = () => {
  const queryClient = useQueryClient();
  const setCampaignStatus = useSetCampaignStatus();

  const [reachPublishers, setReachPublishers] = useState<
    ActiveSourcingChannelDetails[]
  >([]);

  /**
   * When a publisher is toggled, we need to update the status of the reach campaign
   * If any one publisher is active and the reach campaign is stopped, set the reach campaign to active
   * If a publisher is stopped, check if any other publisher is active and if not, set the reach campaign to stopped
   */
  const setReachPublisherStatus = useCallback(
    (
      sourcingPurchaseId: string,
      publisherId: string,
      publisherStatus: SourcingChannelStatus,
    ) => {
      setReachPublishers(prevPublishers =>
        prevPublishers.map(publisher =>
          publisher.reach_campaign_id === publisherId
            ? { ...publisher, status: publisherStatus }
            : publisher,
        ),
      );

      const channelsData =
        queryClient.getQueryData<SourcingOpeningChannelsResponse>([
          ...queryKeys.openingDetails.channels.list,
        ]);
      const reachCampaign = channelsData?.active_channels.find(
        campaign => campaign.sourcing_purchase_id === sourcingPurchaseId,
      );

      if (publisherStatus === 'active' && reachCampaign?.status === 'stopped') {
        setCampaignStatus(sourcingPurchaseId, 'active');
      } else if (publisherStatus === 'stopped') {
        const isAnyPublisherActive = reachPublishers.some(publisher => {
          if (publisher.reach_campaign_id !== publisherId) {
            return publisher.status === 'active';
          }
          return false;
        });

        if (!isAnyPublisherActive) {
          setCampaignStatus(sourcingPurchaseId, 'stopped');
        }
      }
    },
    [queryClient, setCampaignStatus, reachPublishers],
  );

  return {
    reachPublishers,
    setReachPublishers,
    setReachPublisherStatus,
  };
};
